// @safetyqube/sq-ai-client — pure selectors over the conversation state.
//
// Additive convenience views the React `useSqAi()` hook surfaces (currentChart,
// streamingText, isThinking, …). They DERIVE from the same per-turn state the UI
// already renders via state.messages — they introduce no new behaviour and no new
// state. "Current" means the latest (last) assistant turn.

function lastAssistant(state) {
  const msgs = state && state.messages;
  if (!msgs) return null;
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i].role === "assistant") return msgs[i];
  }
  return null;
}

export function selectIsThinking(state) {
  return !!state && state.status === "in_flight";
}

// The active (or last) "Thinking…" step of the current turn, or null.
export function selectThinkingPhase(state) {
  const m = lastAssistant(state);
  if (!m) return null;
  const steps = m.thinkingSteps || [];
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].status === "live") return steps[i];
  }
  return steps.length ? steps[steps.length - 1] : null;
}

// The latest chart spec on the current turn (the testing UI renders ALL of
// message.charts; this is a convenience for single-chart consumers).
export function selectCurrentChart(state) {
  const m = lastAssistant(state);
  if (!m || !m.charts || !m.charts.length) return null;
  return m.charts[m.charts.length - 1];
}

export function selectCharts(state) {
  const m = lastAssistant(state);
  return (m && m.charts) || [];
}

export function selectStreamingText(state) {
  const m = lastAssistant(state);
  return (m && m.caption) || "";
}

export function selectAssumptions(state) {
  const m = lastAssistant(state);
  return (m && m.assumptions) || [];
}

export function selectResultMeta(state) {
  const m = lastAssistant(state);
  return (m && m.resultMeta) || null;
}

// The dev token/cost block of the latest assistant turn, or null. Null is the DEFAULT and
// the safe reading: the server builds this only for the switches an operator turned on and
// omits the rest, so there is nothing here to hide and nothing a caller can turn on.
export function selectCostDisplay(state) {
  const m = lastAssistant(state);
  return (m && m.costDisplay) || null;
}

export function selectError(state) {
  const m = lastAssistant(state);
  return (m && m.error) || null;
}

// The CSV export descriptor of the latest assistant turn (the answering data result),
// or null on a non-data turn. v1 emits at most one per turn; this returns the first.
// The consumer renders its own export button against this (see the lab's Thread).
export function selectLatestExport(state) {
  const m = lastAssistant(state);
  const ex = m && m.exports;
  return Array.isArray(ex) && ex.length ? ex[0] : null;
}

// reconcileRenderSurfaces — ONE table per turn. A data turn can produce up to three
// overlapping surfaces: the LLM's streamed caption (which may contain a markdown
// table), a chart (render_chart → message.charts), and the structured export panel
// (ai:done.exports → the preview table + Download-CSV). Rendered naively they double
// up (a listing shows the markdown table AND the export panel; a chart shows the
// chart AND the export panel). This reconciles them purely from what the turn
// produced, so the consumer renders a single non-redundant answer:
//   · CHART intent — a "real" chart is present, i.e. any chart_type EXCEPT `table`
//     (pie / bar / line / donut / stacked_bar / kpi). The chart + the LLM's text
//     are the answer; the structured export table + CSV would duplicate the data, so
//     it is suppressed (a chart answer carries NO table and NO CSV).
//   · LIST intent — no real chart, but an export descriptor is present. The structured
//     export table + CSV is the canonical row display (the chart-mode prompt tells the
//     LLM to reply with a one-line summary and no markdown table). A `table` chart is a
//     listing surface, never a "real" chart, so it is dropped here — a listing never
//     shows two tables, and no rows are lost because the export panel carries them.
//   · none — neither a chart nor an export descriptor (a refusal, a single-record
//     narrative): nothing is suppressed; the caption is the whole answer.
// A `table` chart is rendered ONLY when it is the sole row display (no export panel to
// take its place), so we never drop rows nor double a table.
export function reconcileRenderSurfaces(message) {
  const charts = message && Array.isArray(message.charts) ? message.charts : [];
  const exports = message && message.exports;
  const exportDescriptor = Array.isArray(exports) && exports.length ? exports[0] : null;
  const realCharts = charts.filter((c) => c && c.chart_type && c.chart_type !== "table");
  const hasRealChart = realCharts.length > 0;
  const showExportPanel = !!exportDescriptor && !hasRealChart;
  const chartsToRender = hasRealChart ? realCharts : showExportPanel ? [] : charts;
  return {
    exportDescriptor, // always the turn's descriptor (or null) — for the chart's own PNG/PDF caption too
    showExportPanel, // whether to render the structured export table + Download CSV
    chartsToRender, // the charts to actually render (real charts, or a lone table chart)
    intent: hasRealChart ? "chart" : exportDescriptor ? "list" : "none",
  };
}
